import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { loadStripe } from '@stripe/stripe-js';
import { useElements, AddressElement, Elements } from '@stripe/react-stripe-js';
import * as actions from 'containers/Projects/actions';
import * as billingActions from 'containers/Billing/actions';
import APModal from 'components/Modal/APModal';
import { FadeLoader } from 'react-spinners';
import { styledTheme } from 'globalStyles/theme';
import { CLOSE_UPDATE_MODAL } from 'containers/Billing/types';
import clsx from 'clsx';

const UpdateAddress = () => {
  const dispatch = useDispatch();

  const { getBillingInfo } = bindActionCreators(billingActions, dispatch);
  const { getProjectLimits } = bindActionCreators(actions, dispatch);

  const userInfo = useSelector((state) => state.global.userInfo);
  const customerAddress = useSelector(state => state.billing.info?.stripeCustomerAddress);
  const projectsLimit = useSelector((state) => state.projects.projectsLimit);
  const addressUpdated = useSelector((state) => state.billing.addressUpdated);

  const isTrial = userInfo?.billing?.planningSubscription?.isTrial;

  const [addressModalIsOpen, setAddressModalIsOpen] = useState(false);

  useEffect(() => {
    getProjectLimits();
    getBillingInfo();
  }, []);

  useEffect(() => {
    // If not US country, don't require city or postal code.
    const isAddressModal = projectsLimit?.isOwner && !isTrial
      && !(
        (customerAddress?.country !== 'US' || customerAddress?.city)
        && customerAddress?.country
        && customerAddress?.line1
        && (customerAddress?.country !== 'US' || customerAddress?.postalCode)
      );

    setAddressModalIsOpen(isAddressModal);
  }, [customerAddress]);

  return (
    addressUpdated ? (
      <APModal
        open={addressUpdated}
        onClose={() => {
          dispatch({ type: CLOSE_UPDATE_MODAL });
          setAddressModalIsOpen(false);
        }}
        title="Thank you for updating your Billing Address"
        actions={(
          <button
            className="button"
            onClick={() => {
              dispatch({ type: CLOSE_UPDATE_MODAL });
              setAddressModalIsOpen(false);
            }}
          >
            OK
          </button>
        )}
      />
    ) : (
      <AddressModal
        open={addressModalIsOpen}
        close={() => setAddressModalIsOpen(false)}
      />
    )
  );
};


const AddressModal = ({ open, close }) => {
  const dispatch = useDispatch();
  const { createSetupIntent, updateCustomerAddress } = bindActionCreators(billingActions, dispatch);

  const billing = useSelector(state => state.billing);

  useEffect(() => {
    if (open) {
      createSetupIntent();
    }
  }, [open]);

  const stripePromise = useMemo(() => loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY), []);
  const optionsElements = {
    clientSecret: billing.clientSecret,
  };

  return (
    <APModal
      open={open}
      onClose={() => close()}
      title='Update required.'
    >
      {billing.clientSecret ? (
        <>
          <Elements
            stripe={stripePromise}
            options={optionsElements}
          >
            <AddressForm
              updateCustomerAddress={updateCustomerAddress}
            />
          </Elements>
        </>
        ) : (
          <section
            className="app_loader"
            style={{ marginTop: '50px', position: 'unset' }}
          >
            <FadeLoader
              color={styledTheme.lighterGray}
              loading
              height={15}
              width={5}
              radius={5}
              margin={2}
            />
          </section>
        )}
    </APModal>
  );
};

const AddressForm = ({ updateCustomerAddress }) => {
  const elements = useElements();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if(!elements) return;

    const addressElement = elements.getElement('address');

    const result = await addressElement.getValue();

    if (!result.complete) return;

    updateCustomerAddress(result.value.address);
  };

  const optionsAddressElement = {
    mode: 'billing',
  };

  return (
    <>
      {elements ? (
        <>
          <form onSubmit={handleSubmit}>
            <div className='address-intro'>
              Some US states now require us to collect sales tax on Aisle Planner subscriptions.
              To ensure that we are only charging those who should pay those taxes, please update
              your full billing address below.
            </div>
            <AddressElement options={optionsAddressElement} />
            <button
              className={clsx('button', 'address-button')}
            >
              Update Billing Address
            </button>
          </form>
        </>
      ) : (
        <section
          className="app_loader"
          style={{ marginTop: '50px', position: 'unset' }}
        >
          <FadeLoader
            color={styledTheme.lighterGray}
            loading
            height={15}
            width={5}
            radius={5}
            margin={2}
          />
        </section>
      )}
    </>
  );
};

export default UpdateAddress;
